// The capable-to-promise dialog (spec 03 §4.9, D-474 ruling 2), shared by the
// promises screen and module 09's composer and order detail through
// `../public.ts`. Product, quantity, the date asked for and the facility;
// Check date runs row 74 and shows the proposed and earliest dates, the path,
// the basis as a checklist and the off-cut option. A quote holds nothing, so
// Close simply leaves it to expire. Use in quote hands the quote to the
// composer; Commit to the order line runs row 75 when the dialog is bound to a
// line. A `BASIS_STALE` answer offers the fresh date the server already
// quoted; a 422 names its reasons, and a 429 says the planner is busy. On a
// phone the dialog fills the screen.
//
// RE-PROMISE (P3-J J-3, spec 09 §4.7, D-469). Module 09's order detail opens
// the same dialog on a line that already carries a date, with `rePromise`:
// the quote shows the line's date beside the new one, and the commit becomes
// Re-promise with a "Tell the customer" toggle that is off by default. The
// host makes the call (`POST /v1/orders/{id}/re-promise`), because the route
// is module 09's; its BASIS_STALE answer reads exactly as row 75's does.

import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import type { RoleInput } from "../../../../lib/role-codes";
import { derivePlanningPermissions, usePlanningPermissions } from "../hooks/permissions";
import {
  usePlanningFacilities,
  usePlanningSettings,
  useProductIndex,
  useUoms,
  type Facility,
} from "../hooks/planning-queries";
import {
  commitPromise,
  quotePromise,
  usePromiseRefresh,
  type CtpPromise,
  type CtpQuoteResult,
} from "../hooks/promise-queries";
import { formatDate, useOffline } from "../lib/planning-lib";
import {
  LINE_REFUSAL_REASONS,
  NO_DATE_REASONS,
  STALE_REASONS,
  knownKeys,
  parsePromiseQty,
  promiseRefusalKey,
} from "../lib/promise-lib";
import { BasisChecklist, PromisePathChip, promiseProductText } from "./promise-bits";
import { ProductPicker } from "./product-picker";

/**
 * Re-promise (spec 09 §4.7, D-469): the line's current date, and the host's
 * call of module 09's re-promise route with the quoted promise. It throws the
 * route's refusal as a `RequestError`.
 */
export interface CtpRePromise {
  readonly oldDate: string | null;
  readonly submit: (promiseId: string, notifyCustomer: boolean) => Promise<void>;
}

/** The order line a dialog commits onto (the order detail's line, D-474). */
export interface CtpDialogLine {
  readonly id: string;
  /** "SO-2026-00012 · line 2", in the host's words. */
  readonly label?: string | undefined;
}

export interface CtpDialogProps {
  /** The facility the dialog opens on; the reader may pick another. */
  readonly facilityId?: string | undefined;
  readonly productId?: string | undefined;
  /** The quantity in the product's base unit, as a decimal string. */
  readonly qty?: string | undefined;
  readonly requestedDate?: string | undefined;
  /** Module 09's quote number for a pre-order quote. */
  readonly quoteRef?: string | undefined;
  /** The saved quote line Check date was pressed on, written to the promise (D-465 ruling 5). */
  readonly quoteLineId?: string | undefined;
  /** Binds the dialog to an order line: the quote names it and Commit is offered. */
  readonly line?: CtpDialogLine | undefined;
  /** The composer's Use in quote. Absent, the button is not drawn. */
  readonly onUse?: ((quote: CtpQuoteResult) => void) | undefined;
  /** After a commit, with the committed promise. */
  readonly onCommitted?: ((promise: CtpPromise) => void) | undefined;
  /** Turns Commit into Re-promise through module 09's route (needs `line`). */
  readonly rePromise?: CtpRePromise | undefined;
  readonly onClose: () => void;
}

/**
 * Whether this caller may run the dialog at all (`planning.promise.commit`,
 * D-464 ruling 1). Module 09 asks with the role signal its own permission
 * hook already holds, before it draws Check date, so a reader without the
 * code sees no button rather than a dialog that refuses them (D-586: read
 * off the feed's permission list, so a clone that holds the code is offered
 * it).
 */
export function canRunCtp(roles: RoleInput): boolean {
  return derivePlanningPermissions(roles).has("planning.promise.commit");
}

/** What the dialog is showing below its form. */
type Answer =
  | { readonly kind: "none" }
  | { readonly kind: "quoted"; readonly quote: CtpQuoteResult }
  | {
      readonly kind: "no_date";
      readonly reasons: readonly string[];
      readonly covered?: string | undefined;
      readonly shortfall?: string | undefined;
    }
  | { readonly kind: "busy" }
  | { readonly kind: "failed"; readonly error: unknown }
  | {
      readonly kind: "stale";
      readonly quote: CtpQuoteResult;
      readonly fresh: CtpPromise;
      readonly reasons: readonly string[];
    }
  | { readonly kind: "refused"; readonly quote: CtpQuoteResult; readonly error: unknown }
  | { readonly kind: "committed"; readonly promise: CtpPromise }
  | { readonly kind: "re_promised"; readonly date: string; readonly notified: boolean };

/** The default date asked for: a week after the facility's today. */
function defaultRequested(facility: Facility | undefined): string {
  return addDaysIso(facilityTodayIso(Date.now(), facility?.timezone), 7);
}

function extra(error: unknown, key: string): unknown {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  return (error.problem as Record<string, unknown>)[key];
}

export function CtpDialog(props: CtpDialogProps) {
  const { t } = useTranslation();
  const perms = usePlanningPermissions();
  const facilities = usePlanningFacilities();
  const list = facilities.data?.items ?? [];
  const canQuote = perms.has("planning.promise.commit");

  return (
    <Modal
      label={t("planning.promises.dialog.title")}
      heading={t("planning.promises.dialog.title")}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      className="planning-dialog planning-ctp-dialog"
      testId="planning-ctp-dialog"
    >
      {!perms.isResolved || facilities.isPending ? (
        <p className="planning-note">{t("planning.common.loading")}</p>
      ) : !canQuote ? (
        <p className="planning-note" data-testid="planning-ctp-no-code">
          {t("planning.promises.dialog.noCode")}
        </p>
      ) : (
        <CtpBody {...props} facilities={list} />
      )}
    </Modal>
  );
}

function CtpBody(props: CtpDialogProps & { facilities: readonly Facility[] }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const refresh = usePromiseRefresh();
  const { facilities, line } = props;
  const [facilityId, setFacilityId] = useState(props.facilityId ?? facilities[0]?.id ?? "");
  const facility = facilities.find((f) => f.id === facilityId);
  const [productId, setProductId] = useState<string | undefined>(props.productId);
  const products = useProductIndex(productId !== undefined ? [productId] : []);
  const product = productId !== undefined ? products.byId.get(productId) : undefined;
  const [qtyText, setQtyText] = useState(props.qty ?? "");
  const [requested, setRequested] = useState(props.requestedDate ?? defaultRequested(facility));
  const [offcuts, setOffcuts] = useState(true);
  const [answer, setAnswer] = useState<Answer>({ kind: "none" });
  const [checking, setChecking] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Re-promise's "Tell the customer", off by default (D-469 ruling 3).
  const [notify, setNotify] = useState(false);
  const settings = usePlanningSettings(facilityId);
  const horizonDays = settings.data?.schedule_horizon_days;

  const qty = parsePromiseQty(qtyText);
  const qtyBad = qtyText.trim().length > 0 && qty === undefined;
  const ready =
    productId !== undefined && qty !== undefined && requested.length === 10 && facilityId !== "";
  const busy = checking || committing;
  const lang = i18n.language;

  const onAuth = (error: unknown): boolean => {
    if (error instanceof UnauthorizedError) {
      void navigate({ to: "/login" });
      return true;
    }
    return false;
  };

  const check = async () => {
    if (!ready) return;
    setChecking(true);
    try {
      const quote = await quotePromise({
        facility_id: facilityId,
        product_id: productId,
        qty,
        requested_date: requested,
        allow_offcuts: offcuts,
        ...(line !== undefined ? { sales_order_line_id: line.id } : {}),
        ...(props.quoteRef !== undefined ? { quote_ref: props.quoteRef } : {}),
        ...(props.quoteLineId !== undefined ? { quote_line_id: props.quoteLineId } : {}),
      });
      setAnswer({ kind: "quoted", quote });
      void refresh(facilityId);
    } catch (error) {
      if (onAuth(error)) return;
      if (error instanceof RequestError && error.code === "CTP_BUSY") {
        setAnswer({ kind: "busy" });
      } else if (error instanceof RequestError && error.code === "CAPACITY_PATH_UNAVAILABLE") {
        const material = extra(error, "material") as
          { covered_qty?: unknown; shortfall_qty?: unknown } | undefined;
        setAnswer({
          kind: "no_date",
          reasons: knownKeys(extra(error, "reasons"), NO_DATE_REASONS),
          covered: typeof material?.covered_qty === "string" ? material.covered_qty : undefined,
          shortfall:
            typeof material?.shortfall_qty === "string" ? material.shortfall_qty : undefined,
        });
      } else {
        setAnswer({ kind: "failed", error });
      }
    } finally {
      setChecking(false);
    }
  };

  const commit = async (
    quote: CtpQuoteResult,
    target: { readonly id: string; readonly promised_date: string },
  ) => {
    if (line === undefined) return;
    const promiseId = target.id;
    setCommitting(true);
    try {
      if (props.rePromise !== undefined) {
        await props.rePromise.submit(promiseId, notify);
        setAnswer({ kind: "re_promised", date: target.promised_date, notified: notify });
        void refresh(facilityId, [quote.id, promiseId]);
      } else {
        const promise = await commitPromise(promiseId, line.id);
        setAnswer({ kind: "committed", promise });
        void refresh(facilityId, [quote.id, promiseId]);
        props.onCommitted?.(promise);
      }
    } catch (error) {
      if (onAuth(error)) return;
      const fresh = extra(error, "fresh_promise") as CtpPromise | undefined;
      if (error instanceof RequestError && error.code === "BASIS_STALE" && fresh !== undefined) {
        setAnswer({
          kind: "stale",
          quote,
          fresh,
          reasons: knownKeys(extra(error, "stale_reasons"), STALE_REASONS),
        });
      } else {
        setAnswer({ kind: "refused", quote, error });
      }
    } finally {
      setCommitting(false);
    }
  };

  const productText = promiseProductText(null, product);

  return (
    <div className="planning-ctp" data-testid="planning-ctp" data-answer={answer.kind}>
      <div className="planning-ctp-form">
        {line !== undefined ? (
          <p className="planning-note" data-testid="planning-ctp-line" data-line-id={line.id}>
            {`${t("planning.promises.dialog.line")}: ${line.label ?? t("planning.promises.salesOrder")}`}
          </p>
        ) : null}
        {productId !== undefined ? (
          <Field label={t("planning.promises.dialog.product")} wide>
            <span
              className="planning-chips"
              data-testid="planning-ctp-product"
              data-product-id={productId}
            >
              <span>{productText ?? "…"}</span>
              {props.productId === undefined ? (
                <Button
                  size="sm"
                  disabled={busy}
                  data-testid="planning-ctp-product-change"
                  onClick={() => {
                    setProductId(undefined);
                    setAnswer({ kind: "none" });
                  }}
                >
                  {t("planning.promises.dialog.changeProduct")}
                </Button>
              ) : null}
            </span>
          </Field>
        ) : (
          <ProductPicker
            label={t("planning.promises.dialog.product")}
            data-testid="planning-ctp-product-picker"
            onPick={(p) => {
              setProductId(p.id);
              setAnswer({ kind: "none" });
            }}
          />
        )}
        <Field label={t("planning.promises.dialog.qty")}>
          <span className="planning-chips">
            <input
              type="text"
              inputMode="decimal"
              value={qtyText}
              disabled={busy}
              aria-invalid={qtyBad}
              data-testid="planning-ctp-qty"
              onChange={(e) => {
                setQtyText(e.target.value);
                setAnswer({ kind: "none" });
              }}
            />
            {product !== undefined ? <BaseQtyUnit productId={product.id} /> : null}
          </span>
        </Field>
        {qtyBad ? (
          <p className="planning-bad" data-testid="planning-ctp-qty-invalid">
            {t("planning.promises.dialog.qtyInvalid")}
          </p>
        ) : null}
        <Field label={t("planning.promises.dialog.requested")}>
          <input
            type="date"
            value={requested}
            disabled={busy}
            data-testid="planning-ctp-requested"
            onChange={(e) => {
              setRequested(e.target.value);
              setAnswer({ kind: "none" });
            }}
          />
        </Field>
        <Field label={t("planning.promises.dialog.facility")}>
          <Select
            value={facilityId}
            disabled={busy || line !== undefined}
            data-testid="planning-ctp-facility"
            onChange={(e) => {
              setFacilityId(e.target.value);
              setAnswer({ kind: "none" });
            }}
          >
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <CheckboxField
          label={t("planning.promises.dialog.offcuts")}
          checked={offcuts}
          disabled={busy}
          data-testid="planning-ctp-offcuts"
          onChange={(checked) => {
            setOffcuts(checked);
            setAnswer({ kind: "none" });
          }}
        />
        {offline ? (
          <p className="planning-note" data-testid="planning-ctp-offline">
            {t("planning.common.offlineWrites")}
          </p>
        ) : null}
        <div className="planning-actions">
          <Button
            variant={answer.kind === "none" ? "primary" : "secondary"}
            busy={checking}
            busyLabel={t("planning.promises.dialog.checking")}
            disabled={!ready || offline || committing}
            data-testid="planning-ctp-check"
            onClick={() => void check()}
          >
            {t("planning.promises.dialog.check")}
          </Button>
        </div>
      </div>
      <AnswerPanel
        answer={answer}
        facility={facility}
        horizonDays={horizonDays}
        lang={lang}
        offline={offline}
        committing={committing}
        canCommit={line !== undefined}
        rePromise={
          props.rePromise !== undefined && line !== undefined
            ? { oldDate: props.rePromise.oldDate, notify, setNotify }
            : undefined
        }
        {...(props.onUse !== undefined ? { onUse: props.onUse } : {})}
        onCommit={(quote, target) => void commit(quote, target)}
        onRetry={() => void check()}
      />
      <div className="planning-actions planning-ctp-close">
        <Button disabled={busy} data-testid="planning-ctp-close" onClick={props.onClose}>
          {t("planning.common.close")}
        </Button>
      </div>
    </div>
  );
}

/** The product's base unit beside the quantity box. */
function BaseQtyUnit({ productId }: { productId: string }) {
  const products = useProductIndex([productId]);
  const uoms = useUoms();
  const product = products.byId.get(productId);
  const code =
    product === undefined
      ? undefined
      : uoms.data?.items.find((u) => u.id === product.base_uom_id)?.code;
  return code === undefined ? null : (
    <span className="planning-unit" data-testid="planning-ctp-unit">
      {code}
    </span>
  );
}

function AnswerPanel({
  answer,
  facility,
  horizonDays,
  lang,
  offline,
  committing,
  canCommit,
  rePromise,
  onUse,
  onCommit,
  onRetry,
}: {
  answer: Answer;
  facility: Facility | undefined;
  horizonDays: number | undefined;
  lang: string;
  offline: boolean;
  committing: boolean;
  canCommit: boolean;
  rePromise: RePromiseView | undefined;
  onUse?: (quote: CtpQuoteResult) => void;
  onCommit: (
    quote: CtpQuoteResult,
    target: { readonly id: string; readonly promised_date: string },
  ) => void;
  onRetry: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const commitLabel =
    rePromise !== undefined
      ? t("planning.promises.dialog.rePromise")
      : t("planning.promises.dialog.commit");
  switch (answer.kind) {
    case "none":
      return null;
    case "busy":
      return (
        <Banner kind="warn" data-testid="planning-ctp-busy">
          <span>{t("planning.promises.refusal.CTP_BUSY")}</span>{" "}
          <Button size="sm" disabled={offline} onClick={onRetry}>
            {t("planning.common.retry")}
          </Button>
        </Banner>
      );
    case "failed":
      return (
        <Banner kind="bad" data-testid="planning-ctp-failed">
          {refusalText(t, answer.error)}
        </Banner>
      );
    case "no_date":
      return (
        <div className="planning-ctp-answer" data-testid="planning-ctp-no-date">
          <p className="planning-bad">{t("planning.promises.dialog.noDate")}</p>
          <ul className="planning-result-list">
            {answer.reasons.map((r) => (
              <li key={r} data-testid="planning-ctp-no-date-reason" data-reason={r}>
                {t(`planning.promises.reason.${r}`)}
              </li>
            ))}
          </ul>
          {answer.covered !== undefined && answer.shortfall !== undefined ? (
            <p className="planning-note" data-testid="planning-ctp-covered">
              {t("planning.promises.dialog.covered", {
                covered: answer.covered,
                shortfall: answer.shortfall,
              })}
            </p>
          ) : null}
        </div>
      );
    case "re_promised":
      return (
        <div
          data-testid="planning-ctp-re-promised"
          data-date={answer.date}
          data-notified={String(answer.notified)}
        >
          <Banner kind="ok">
            {t(
              answer.notified
                ? "planning.promises.dialog.rePromisedTold"
                : "planning.promises.dialog.rePromised",
              { date: formatDate(answer.date, lang) },
            )}
          </Banner>
        </div>
      );
    case "committed":
      return (
        <div data-testid="planning-ctp-committed" data-promise-id={answer.promise.id}>
          <Banner kind="ok">
            {t("planning.promises.dialog.committed", { number: answer.promise.promise_no })}
          </Banner>
        </div>
      );
    case "stale":
      return (
        <div className="planning-ctp-answer" data-testid="planning-ctp-stale">
          <Banner kind="warn">{t("planning.promises.dialog.stale")}</Banner>
          <ul className="planning-result-list">
            {answer.reasons.map((r) => (
              <li key={r} data-reason={r}>
                {t(`planning.promises.staleReason.${r}`)}
              </li>
            ))}
          </ul>
          <p
            className="planning-ctp-date"
            data-testid="planning-ctp-new-date"
            data-date={answer.fresh.promised_date}
          >
            {t("planning.promises.dialog.newDate", {
              date: formatDate(answer.fresh.promised_date, lang),
            })}
          </p>
          {rePromise !== undefined ? <RePromiseFields view={rePromise} lang={lang} /> : null}
          <div className="planning-actions">
            <Button
              variant="primary"
              busy={committing}
              busyLabel={t("planning.promises.dialog.committing")}
              disabled={offline}
              data-testid="planning-ctp-accept-new"
              onClick={() => {
                onCommit(answer.quote, answer.fresh);
              }}
            >
              {t("planning.promises.dialog.acceptNew")}
            </Button>
          </div>
        </div>
      );
    case "quoted":
    case "refused": {
      const quote = answer.quote;
      return (
        <QuoteView
          quote={quote}
          facility={facility}
          horizonDays={horizonDays}
          lang={lang}
          actions={
            <>
              {answer.kind === "refused" ? (
                <Banner kind="bad" data-testid="planning-ctp-refused">
                  {refusalText(t, answer.error)}
                </Banner>
              ) : null}
              {canCommit && rePromise !== undefined ? (
                <RePromiseFields view={rePromise} lang={lang} />
              ) : null}
              <div className="planning-actions">
                {onUse !== undefined ? (
                  <Button
                    variant="primary"
                    disabled={committing}
                    data-testid="planning-ctp-use"
                    onClick={() => {
                      onUse(quote);
                    }}
                  >
                    {t("planning.promises.dialog.use")}
                  </Button>
                ) : null}
                {canCommit ? (
                  <Button
                    variant={onUse !== undefined ? "secondary" : "primary"}
                    busy={committing}
                    busyLabel={t("planning.promises.dialog.committing")}
                    disabled={offline}
                    data-testid={
                      rePromise !== undefined ? "planning-ctp-repromise" : "planning-ctp-commit"
                    }
                    onClick={() => {
                      onCommit(quote, quote);
                    }}
                  >
                    {commitLabel}
                  </Button>
                ) : null}
              </div>
            </>
          }
        />
      );
    }
  }
}

/** Re-promise's half of the answer: the line's date and the toggle. */
interface RePromiseView {
  readonly oldDate: string | null;
  readonly notify: boolean;
  readonly setNotify: (notify: boolean) => void;
}

/**
 * The line's date the new one replaces, beside it (spec 09 §4.7), and "Tell
 * the customer", off by default: risk never messages a customer, and a person
 * chooses each time (D-469 ruling 3).
 */
function RePromiseFields({ view, lang }: { view: RePromiseView; lang: string }) {
  const { t } = useTranslation();
  return (
    <div className="planning-ctp-repromise" data-testid="planning-ctp-repromise-fields">
      <p
        className="planning-note"
        data-testid="planning-ctp-old-date"
        data-date={view.oldDate ?? ""}
      >
        {view.oldDate !== null
          ? t("planning.promises.dialog.oldDate", { date: formatDate(view.oldDate, lang) })
          : t("planning.promises.dialog.oldDateNone")}
      </p>
      <CheckboxField
        label={t("planning.promises.dialog.notifyCustomer")}
        checked={view.notify}
        data-testid="planning-ctp-notify"
        onChange={view.setNotify}
      />
    </div>
  );
}

/** A refusal in words: its own sentence, the line's reason, or the request id. */
export function refusalText(t: TFunction, error: unknown): string {
  const code = error instanceof RequestError ? error.code : undefined;
  const key = promiseRefusalKey(code);
  const base =
    key === "planning.promises.refusal.other"
      ? t(key, { requestId: requestIdOf(error) ?? "-" })
      : t(key);
  if (code === "ORDER_LINE_NOT_PROMISABLE") {
    const [reason] = knownKeys([extra(error, "reason")], LINE_REFUSAL_REASONS);
    return reason !== undefined ? `${base} ${t(`planning.promises.lineReason.${reason}`)}` : base;
  }
  return base;
}

function QuoteView({
  quote,
  facility,
  horizonDays,
  lang,
  actions,
}: {
  quote: CtpQuoteResult;
  facility: Facility | undefined;
  horizonDays: number | undefined;
  lang: string;
  actions: ReactNode;
}) {
  const { t } = useTranslation();
  const option = quote.offcut_option;
  return (
    <div
      className="planning-ctp-answer"
      data-testid="planning-ctp-quote"
      data-promise-id={quote.id}
      data-promise-no={quote.promise_no}
      data-promised-date={quote.promised_date}
      data-earliest-date={quote.earliest_date}
      data-path={quote.fulfillment_path}
      data-feasible={String(quote.feasible_by_requested)}
    >
      <p className="planning-ctp-proposed">
        <span className="planning-note">{t("planning.promises.dialog.proposed")}</span>{" "}
        <strong className="planning-ctp-date" data-testid="planning-ctp-proposed">
          {formatDate(quote.promised_date, lang)}
        </strong>
      </p>
      <p className="planning-chips">
        <StatusChip
          variant={quote.feasible_by_requested ? "ok" : "warn"}
          data-testid="planning-ctp-feasible"
        >
          {quote.feasible_by_requested
            ? t("planning.promises.dialog.byRequested")
            : t("planning.promises.dialog.afterRequested")}
        </StatusChip>
        <PromisePathChip path={quote.fulfillment_path} />
      </p>
      <p className="planning-note" data-testid="planning-ctp-earliest">
        {t("planning.promises.dialog.earliest", { date: formatDate(quote.earliest_date, lang) })}
      </p>
      <BasisChecklist
        basis={quote.basis}
        verdicts={null}
        facilityId={quote.facility_id}
        timezone={facility?.timezone}
        showMarks={false}
        horizonDays={horizonDays}
      />
      {option !== null && option.units.length > 0 ? (
        <section className="planning-ctp-offcuts" data-testid="planning-ctp-offcut-option">
          <h3 className="planning-drawer-sub">{t("planning.promises.dialog.offcutOption")}</h3>
          <ul className="planning-result-list">
            {option.units.map((u) => (
              <li
                key={u.stock_unit_id}
                data-testid="planning-ctp-offcut-unit"
                data-stock-unit-id={u.stock_unit_id}
              >
                {t("planning.promises.dialog.offcutUnit", { length: u.length_m })}
              </li>
            ))}
          </ul>
          <p className="planning-note">
            {t("planning.promises.dialog.waste", { qty: option.waste_qty })}
          </p>
        </section>
      ) : null}
      {quote.valid_until !== null ? (
        <p className="planning-note" data-testid="planning-ctp-valid-until">
          {t("planning.promises.dialog.validUntil", {
            at: formatTimestamp(quote.valid_until, lang, facility?.timezone),
          })}
        </p>
      ) : null}
      {actions}
    </div>
  );
}
